/**
 * The plains opening's fill-ins (lane C0-STORY, docs/research/C0_STORY_BUILD.md): the tassel as a real item, the
 * duel ring and the คู่ฝึก row of the 256 m plains, the townsfolk who stand near the quest people, the fair's demo
 * bouts, and ฉีหมิง's fight in M7. Data only; the rules are systems/plainsStory.ts (and systems/duelGround.ts for the
 * ring). All of it is in force only with the 256 m plains.
 */
import type { DuelGroundDef } from './duelGround';
import type { ItemDef } from './items';

type XY = readonly [x: number, y: number];

/** พู่กระบี่สีแดง: the chief's drop, the brother's tassel (a treasure carried in the bag). */
export const TASSEL_ID = 'red-sword-tassel';
export const TASSEL_ITEM: ItemDef = {
  id: TASSEL_ID,
  name: 'พู่กระบี่สีแดง',
  slot: 'treasure',
  rarity: 'rare',
  desc: 'พู่ไหมแดงเก่า ผูกกับปลอกกระบี่ของพี่ชาย ชายหน้ากากไม้ถือมันมาจากที่ใดสักแห่งทางเหนือ',
  stats: { maxHp: 15 },
  visual: '',
  worth: 0,
};

/** The duel ring of the plains (layout RING at (104, 100), floor radius 6): the floor, its marks and the ring master's gate. */
export const PLAINS_RING: DuelGroundDef = {
  zone: 'plains',
  name: 'ลานประลอง',
  floor: [98, 110, 94, 106],
  gate: [113.5, 102.5],
  marks: [
    [101.5, 100],
    [106.5, 100],
  ],
  regulars: [],
};

/** The คู่ฝึก posts (tiers 1 and 2 stand at the start; the ring is kept clear), on the ring's east arc. */
export const PLAINS_SPAR_SPOTS: readonly XY[] = [
  [116, 94.5],
  [116, 108],
];

/** The คู่ฝึก tier whose first win teaches 颯 (the second: คู่ฝึกขั้นฝึกลมปราณ). */
export const GLIDE_TIER = 'spar-qi';
export const GLIDE_MOVE = 'bs-glide';

export interface FolkNear {
  readonly id: string;
  readonly name: string;
  readonly title: string;
  readonly sprite: string;
  readonly at: XY;
  readonly facing?: 1 | -1;
  /** What they say when talked to, in turn. */
  readonly lines: readonly string[];
}

/** Townsfolk beside the quest people, so the ring, the cart, the hall and the training hall do not stand empty. */
export const FOLK_NEAR: readonly FolkNear[] = [
  {
    id: 'cart-kid',
    name: 'อาเหม่ย',
    title: 'เด็กข้างเกวียน',
    sprite: 'townKid',
    at: [124.5, 146.5],
    lines: ['พี่ชนะสองยกก่อนก็ชนะแล้วนะ! / ข้าดูมาทั้งวัน', 'ผู้คุมลานดุ แต่ใจดีนะ'],
  },
  {
    id: 'cart-aunt',
    name: 'ป้าสุ',
    title: 'คนขายผักข้างเกวียน',
    sprite: 'farmerAunt',
    at: [128.5, 150.5],
    lines: ['ลุงเกาแกลากเกวียนมาแต่เช้า เมื่อคืนแกไม่หลับ / บอกว่าเห็นแสงร่วง', 'ฝนหยุดแล้ว ผักก็ขายได้'],
  },
  {
    id: 'hall-tea',
    name: 'ป้าชา',
    title: 'คนขายน้ำชา',
    sprite: 'teaSeller',
    at: [91.5, 87],
    lines: ['ชุมนุมรับศิษย์วันนี้คนเยอะ / ชาร้อนๆ ไหมเจ้าคะ', 'ฟังทุกสำนักก่อนค่อยเลือก ไม่เสียหาย'],
  },
  {
    id: 'hall-girl',
    name: 'อาหลิน',
    title: 'เด็กหญิงดูธง',
    sprite: 'townGirl',
    at: [78, 88.5],
    lines: ['ธงสีแดงใหญ่ที่สุด! / แต่ท่านผู้เฒ่าธงฟ้าใจดีกว่า', 'ข้าอยากเป็นศิษย์สำนักไหนก็ได้'],
  },
  {
    id: 'hall-fisher',
    name: 'ตาปลา',
    title: 'ชาวประมงแก่',
    sprite: 'fisherOld',
    at: [88.5, 117.5],
    lines: ['ครูหวังสอนตัวเบาจนข้าวิ่งตามไม่ทัน / ข้าก็เลยไปตกปลาดีกว่า'],
  },
];

/** The fair's demo bouts: pairs of recruiters (story roles or school masters) who trade blows when the hero is near. */
export const FAIR_DEMO = {
  /** Metres from either recruiter at which the demo plays. */
  near: 16,
  /** Seconds between blows of a pair, and between a pair's rounds. */
  blow: 1.6,
  /** Pairs by the school ids and roles (`tide` is the คลื่นไผ่ elder). */
  pairs: [
    ['cinnabar', 'mist-peak'],
    ['pine-ink', 'tide'],
  ] as readonly (readonly [string, string])[],
  line: 'ดูท่านี้! นี่คือวิชาของสำนักข้า',
} as const;

/** ฉีหมิง's fight beside the hero in M7. */
export const QIMING_FIGHT = {
  /** Reach of his sight of a foe (metres from him), and his blow's reach and rhythm. */
  sight: 14,
  reach: 1.6,
  blow: 1.3,
  atk: 16,
  /** He never takes a foe below this share of its health: the hero finishes it. */
  leaveHp: 0.3,
} as const;

/** The fair's trial bouts: a recruiter lends the hero his school's first art for one soft bout against a sparring lad. */
export const FAIR_TRIAL = {
  /** The sparring lad (data/monsters.ts) and the share of his health at which the bout is won. */
  foe: 'sparrer-yard',
  foeHp: 1500,
  /** The bout is won once this many different lent moves were used and the lad is down to this share of health. */
  moves: 3,
  winAt: 0.35,
  /** Until the moves are used he cannot fall below this share. */
  floorHp: 0.4,
  /** His rhythm: a swing every `swing` s, a guard raised every `guard` s, a charged break every `crush` s. */
  swing: 2.4,
  guard: 7,
  crush: 9,
  /** The bout ends (nothing lost) when the hero drops to this share of health, strays this far, or after this long. */
  hurtAt: 0.2,
  stray: 24,
  maxSeconds: 240,
  button: 'ลองประลอง',
} as const;

/** Quest people's spots on the 256 m plains: npcLife keeps its places this far (plus their radius) from each. */
export const QUEST_CLEAR_R = 3.5;
export const QUEST_KEEP_CLEAR: readonly XY[] = [
  [131.5, 148.5], // ลุงเกา
  [96.5, 100], // ฉีหมิง
  [113.5, 102.5], // เหล่ยกัง
  [86.5, 114.5], // ครูฝึกเฒ่าหวัง
  [75.5, 84.5],
  [84.5, 84.5],
  [98.5, 84.5], // the fair
  [74, 57], // ฉีหมิง in M7
  [128.5, 128.5], // the stele and the elder
];

/** ฉีหมิง's spar: how often he leads (seconds), by the form being learned (0 block, 1 break his guard, 2 cut his charge). */
export const SPAR_RHYTHM = { slash: 2.2, guard: 3.2, crush: 3.6, engage: 7 } as const;

/** The masked chief's phases (share of his health). Each opens only once the hero has answered it, or after `gateSeconds`. */
export const CHIEF = {
  /** Above this share he holds his guard: break it (a guard break) to go on. */
  guardFloor: 0.66,
  /** Above this share he charges: interrupt a charge to go on. */
  chargeFloor: 0.33,
  /** A phase that has held this long opens by itself (a fresh art may have no answer). */
  gateSeconds: 30,
  /** Seconds between his guard-ups (phase 1) and charges (phase 2; phase 3 both, faster). */
  guardEvery: 3.5,
  chargeEvery: 4.2,
  rageSpeed: 1.25,
  hp: 1100,
} as const;

/** The gear the chief drops: a rolled weapon of the hero's kind (rare, item level 8). */
export const CHIEF_DROP = { rarity: 'rare', ilv: 8 } as const;
